import React from "react";
//#region types
import { TableListInnerProps } from "models/propTypes/inputPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import useSearchContext from "../../../search/context/useContext";
import TableAsync from "../../../tables/tableAsync";
import SimpleExpansionPanel from "../../../panels/simpleExpansionPanel";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function TableListInner<T extends Record<string, any>>(props: TableListInnerProps<T>) {
  const {
    value,
    properties,
    isReadOnly,
    disableItemClick,
    activeKey,
    searchKeys,
    inactiveGroupTitle,
    hideHeader,
    orderByProperty,
    disableSort,
    onItemClick,
    stayOnCurrentPage,
    initialPage,
    onCurrentPageChange,
    onSearchTextChange,
  } = props;

  const [inactiveItems, setInactiveItems] = React.useState<any[]>(
    activeKey ? value?.filter((x: any) => !x[activeKey]) : []
  );
  const [activeItems, setActiveItems] = React.useState<any[]>(
    activeKey ? value?.filter((x: any) => x[activeKey]) : value
  );

  const searchContext = useSearchContext();

  React.useEffect(() => {
    let items = value;
    if (searchKeys) {
      const pattern = searchContext?.searchText?.toLowerCase() ?? "";
      // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
      const keys = searchKeys != undefined ? searchKeys : [];

      items = value?.filter((x: any) => isAMatch(x, pattern, keys));
    }
    setActiveItems(activeKey ? items?.filter((x: any) => x[activeKey]) : items);
    setInactiveItems(activeKey ? items?.filter((x: any) => !x[activeKey]) : []);
  }, [searchContext?.searchText, searchKeys, activeKey, value]);

  React.useEffect(() => {
    onSearchTextChange?.(searchContext?.searchText);
  }, [searchContext?.searchText]);

  // Searched fields are often optional on the entity (a user may lack an email or
  // personal identity number), so a missing value must not break the whole list.
  const isAMatch = (x: T, pattern: string, searchKeys: (keyof T)[]) =>
    searchKeys.some((searchKey) => {
      const value = String(x[searchKey] ?? "").toLowerCase();
      return value.includes(pattern) || matchesIgnoringDigitSeparators(value, pattern);
    });

  // A personal identity number is stored as 950524-0029 but people type it either way,
  // so a search made up of digits also matches with every separator removed. Patterns
  // without digits are left alone, and a twelve-digit pattern also matches the ten-digit
  // form so 199505240029 finds 950524-0029.
  const matchesIgnoringDigitSeparators = (value: string, pattern: string) => {
    const patternDigits = digitsOf(pattern);
    if (!patternDigits) return false;

    const valueDigits = digitsOf(value);
    if (valueDigits.includes(patternDigits)) return true;

    return /^(19|20)\d{10}$/.test(patternDigits) && valueDigits.includes(patternDigits.slice(2));
  };

  const digitsOf = (text: string) => text.replace(/\D/g, "");

  return (
    <>
      <TableAsync<T>
        properties={properties}
        rowClickHandler={!isReadOnly && !disableItemClick ? onItemClick : undefined}
        filter={activeItems}
        pagination
        disableQueryPagination
        maxRowCount={40}
        hideHeader={hideHeader ?? true}
        orderByProperty={orderByProperty}
        disableSort={disableSort}
        hidePaginationIfSinglePage
        stayOnCurrentPage={stayOnCurrentPage}
        pageResetKey={searchContext?.searchText}
        initialPage={initialPage}
        onCurrentPageChange={onCurrentPageChange}
      />
      {activeKey && inactiveItems.length !== 0 && (
        <SimpleExpansionPanel
          title={`${LocalizationHelperInstance.getFormattedMessage(
            inactiveGroupTitle ?? TranslationKeys.Common.Disabled
          )} (${inactiveItems.length})`}
          borderless
          className={cssExports.inactiveList}
        >
          {inactiveItems.length > 0 && (
            <TableAsync<T>
              properties={properties}
              rowClickHandler={!isReadOnly && !disableItemClick ? onItemClick : undefined}
              filter={activeKey ? inactiveItems : []}
              pagination
              disableQueryPagination
              maxRowCount={40}
              hideHeader
              orderByProperty={orderByProperty}
              disableSort={disableSort}
              hidePaginationIfSinglePage
              stayOnCurrentPage={stayOnCurrentPage}
              pageResetKey={searchContext?.searchText}
            />
          )}
        </SimpleExpansionPanel>
      )}
    </>
  );
}

export default TableListInner;
